import {
  type Row,
  type RowData,
  type Table,
  flexRender,
} from "@tanstack/react-table";
import {
  type VirtualItem,
  type Virtualizer,
  useVirtualizer,
} from "@tanstack/react-virtual";
import { noop } from "lodash";
import {
  type PropsWithChildren,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import type { ZodType, ZodTypeDef } from "zod";

import { cn } from "@/lib/utils";
import { useSpinDelay } from "spin-delay";
import { ValidationRoot } from "../shared/form/shared/form-validation";
import {
  TableBody as RawTableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

type Props<T = RowData> = {
  onRowClick?: (event: React.MouseEvent, row: Row<T>, index: number) => void;
  table: Table<T>;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  clickableColumns?: string[];
  estimateSize?: number;
  header?: "sticky" | "static";
  loading: boolean;
  rowValidationSchema?: ZodType<any, ZodTypeDef, any>;
  stickyColumns?: {
    left?: string[];
    right?: string[];
  };
  children?: (props: {
    rows: Row<T>[];
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
    table: Table<T>;
    clickableColumns: string[];
    onRowClick: (event: React.MouseEvent, row: Row<T>, index: number) => void;
    rowValidationSchema?: ZodType<any, ZodTypeDef, any>;
  }) => React.ReactNode;
  hideHeader?: boolean;
  footer?: React.ReactNode | false;
  getSubRowStyle?: (row: Row<T>) => string | boolean;
  bordered?: boolean;
  rowClassName?: React.HTMLAttributes<HTMLTableRowElement>["className"];
  getEmptyStateComponent?: (table: Table<T>) => React.ReactNode;
};

// Helper function to calculate sticky column positioning
const getStickyColumnPosition = (
  columnId: string,
  visibleItems: Array<{ column: { id: string; columnDef: { size?: number } } }>,
  stickyColumns?: {
    left?: string[];
    right?: string[];
  },
  hovered?: boolean, // new param to indicate if the row is hovered
) => {
  const isStickyLeft = stickyColumns?.left?.includes(columnId);
  const isStickyRight = stickyColumns?.right?.includes(columnId);

  let leftOffset = 0;
  let isRightmostLeftSticky = false;
  let isLeftmostRightSticky = false;
  let isLeftmostLeftSticky = false;

  if (isStickyLeft && stickyColumns?.left) {
    const stickyIndex = stickyColumns.left.indexOf(columnId);
    if (stickyIndex > 0) {
      // Calculate the cumulative width of all previous sticky columns
      const previousStickyColumns = stickyColumns.left.slice(0, stickyIndex);
      leftOffset = previousStickyColumns.reduce((total, columnId) => {
        const column = visibleItems.find((c) => c.column.id === columnId);
        return total + (column?.column.columnDef.size || 0);
      }, 0);
    }
    // Check if this is the rightmost left sticky column
    isRightmostLeftSticky = stickyIndex === stickyColumns.left.length - 1;
    // Check if this is the leftmost left sticky column
    isLeftmostLeftSticky = stickyIndex === 0;
  }

  if (isStickyRight && stickyColumns?.right) {
    const stickyIndex = stickyColumns.right.indexOf(columnId);
    // Check if this is the leftmost right sticky column
    isLeftmostRightSticky = stickyIndex === 0;
  }

  return {
    isStickyLeft,
    isStickyRight,
    leftOffset,
    isRightmostLeftSticky,
    isLeftmostRightSticky,
    isLeftmostLeftSticky,
  };
};

const RowWrapper = <T extends RowData>({
  schema,
  children,
  row,
}: PropsWithChildren<{
  schema?: ZodType<any, ZodTypeDef, any>;
  row: Row<T>;
}>) => {
  if (!schema) {
    return <>{children}</>;
  }

  return (
    <ValidationRoot schema={schema} values={row.original}>
      {children}
    </ValidationRoot>
  );
};

type TableBodyRowProps<T extends RowData> = {
  row: Row<T>;
  virtualRow: VirtualItem;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  table: Table<T>;
  clickableColumns: string[];
  onRowClick: (event: React.MouseEvent, row: Row<T>, index: number) => void;
  rowValidationSchema?: ZodType<any, ZodTypeDef, any>;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  stickyColumns?: {
    left?: string[];
    right?: string[];
  };
  getSubRowStyle?: (row: Row<T>) => string | boolean;
  bordered?: boolean;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
};

const _TableBodyRow = <T extends RowData>({
  row,
  virtualRow,
  rowVirtualizer,
  table,
  onRowClick,
  rowValidationSchema,
  className,
  stickyColumns,
  getSubRowStyle,
  bordered = false,
  onMouseEnter: externalMouseEnter,
  onMouseLeave: externalMouseLeave,
}: TableBodyRowProps<T>) => {
  const [hoveredRow, setHoveredRow] = useState<string>();

  const handleRowClick = useCallback(
    (event: React.MouseEvent) => {
      const target = event.target as HTMLElement;

      const tableCell = target.closest("[data-table-cell]");

      const isInteractive = tableCell
        ?.querySelector(
          'button, [role="button"], a:not([data-popover-trigger]), input, select, textarea, [contenteditable="true"], [tabindex="0"]',
        )
        ?.contains(target);

      if (isInteractive) {
        return;
      }

      onRowClick(event, row, row.index);
    },
    [row, onRowClick],
  );

  const defaultGetSubRowStyle = (row: Row<T>) => {
    return row.depth > 0 && row.depth % 2 === 0 && "bg-slate-50";
  };

  const displaySubRowStyle = getSubRowStyle ?? defaultGetSubRowStyle;

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      setHoveredRow(row.id);
      externalMouseEnter?.(e);
    },
    [row.id, externalMouseEnter],
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent) => {
      setHoveredRow(undefined);
      externalMouseLeave?.(e);
    },
    [externalMouseLeave],
  );

  return (
    <RowWrapper schema={rowValidationSchema} row={row} key={row.id}>
      <TableRow
        data-index={virtualRow.index} //needed for dynamic row height measurement
        ref={(node) => rowVirtualizer.measureElement(node)} //measure dynamic row height
        key={row.id}
        className={cn(
          "min-h-[52px]",
          table?.getFocusedRowId?.() === row.id && "bg-slate-100",
          row.getIsSelected() &&
            "bg-blue-50 shadow-[inset_2px_0_0_0_rgb(59_130_246)]",
          displaySubRowStyle(row),
          className,
        )}
        style={{
          display: "flex",
          alignItems: "center",
          position: "absolute",
          transform: `translateY(${virtualRow.start}px)`,
          width: "100%",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleRowClick}
      >
        {row.getVisibleCells().map((cell, cellIndex) => {
          const meta = cell.getContext().cell.column.columnDef.meta;
          const hasSize = cell.column.columnDef.size !== undefined;

          const {
            isStickyLeft,
            isStickyRight,
            leftOffset,
            isRightmostLeftSticky,
            isLeftmostRightSticky,
          } = getStickyColumnPosition(
            cell.column.id,
            row.getVisibleCells(),
            stickyColumns,
          );

          // Determine background color based on row state
          const isFocused = table?.getFocusedRowId?.() === row.id;
          const isSelected = row.getIsSelected();
          const isHovered = hoveredRow === row.id;
          const isEvenDepth = row.depth > 0 && row.depth % 2 === 0;

          let bgClass = "";
          if (isHovered) {
            bgClass = "bg-slate-100"; // Hover state overrides others
          } else if (isSelected) {
            bgClass = "bg-blue-50";
          } else if (isFocused) {
            bgClass = "bg-slate-100";
          } else if (isEvenDepth) {
            bgClass = "bg-slate-50";
          } else {
            bgClass = "bg-background";
          }

          return (
            <TableCell
              key={cell.id}
              role={cell.id === "select" ? "checkbox" : undefined}
              className={cn(
                "py-0 px-0.5 flex-[1_1_0] self-stretch flex items-center",
                bordered && "border-r border-border last:border-r-0",
                meta?.className,
                hasSize && "flex-none",
                "transition-colors",
                isStickyLeft && `sticky z-10 ${bgClass} pr-4`,
                isStickyRight && `sticky right-0 z-10 ${bgClass} pl-4`,
                isRightmostLeftSticky && "border-r-2 border-r-border",
                isLeftmostRightSticky && "border-l-2 border-l-border",
                isSelected &&
                  cellIndex === 0 &&
                  "shadow-[inset_2px_0_0_0_rgb(59_130_246)]",
              )}
              data-table-cell
              style={{
                width: cell.column.columnDef.size,
                minWidth:
                  cell.column.columnDef.minSize ?? cell.column.columnDef.size,
                maxWidth:
                  cell.column.columnDef.maxSize === Number.MAX_SAFE_INTEGER
                    ? cell.column.columnDef.size
                    : cell.column.columnDef.maxSize,
                ...(isStickyLeft && { left: `${leftOffset}px` }),
              }}
              align={meta?.align}
            >
              <div className="w-full">
                {flexRender(cell.column.columnDef.cell, {
                  ...cell.getContext(),
                  isHovered: hoveredRow === row.id,
                })}
              </div>
            </TableCell>
          );
        })}
      </TableRow>
    </RowWrapper>
  );
};

export const TableBodyRow = memo(_TableBodyRow) as <T extends RowData>(
  props: TableBodyRowProps<T>,
) => React.ReactElement;

export const TableBody = <T extends RowData>({
  loading,
  children,
  rowVirtualizer,
  rows,
  table,
  rowValidationSchema,
  clickableColumns,
  onRowClick,
  stickyColumns,
  getSubRowStyle,
  bordered = false,
  rowClassName,
  getEmptyStateComponent,
}: {
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  rows: Row<T>[];
  table: Table<T>;
  rowValidationSchema?: ZodType<any, ZodTypeDef, any>;
  clickableColumns: string[];
  onRowClick: (event: React.MouseEvent, row: Row<T>, index: number) => void;
  loading: boolean;
  stickyColumns?: {
    left?: string[];
    right?: string[];
  };
  children?: (props: {
    rows: Row<T>[];
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
    table: Table<T>;
    clickableColumns: string[];
    onRowClick: (event: React.MouseEvent, row: Row<T>, index: number) => void;
    rowValidationSchema?: ZodType<any, ZodTypeDef, any>;
    emptyStateComponent?: React.ReactNode;
  }) => React.ReactNode;
  getSubRowStyle?: (row: Row<T>) => string | boolean;
  rowClassName?: React.HTMLAttributes<HTMLTableRowElement>["className"];
  bordered?: boolean;
  getEmptyStateComponent?: (table: Table<T>) => React.ReactNode;
}) => {
  const { t } = useTranslation();

  const defaultEmptyStateComponent = () => {
    return (
      <RawTableBody>
        <TableRow className="w-full flex justify-center">
          <TableCell className="py-5">{t("no_results")}</TableCell>
        </TableRow>
      </RawTableBody>
    );
  };

  const isEmpty = table.getPreFilteredRowModel().rows.length === 0 && !loading;
  const showEmptyState = useSpinDelay(isEmpty, {
    delay: 600,
    minDuration: 400,
  });

  if (showEmptyState) {
    const getEmptyStateComponentRenderer = getEmptyStateComponent
      ? getEmptyStateComponent
      : defaultEmptyStateComponent;

    return <RawTableBody>{getEmptyStateComponentRenderer(table)}</RawTableBody>;
  }

  return (
    <RawTableBody
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`, //tells scrollbar how big the table is
        position: "relative", //needed for absolute positioning of rows
        width: "100%",
      }}
    >
      {children
        ? children({
            rows,
            rowVirtualizer,
            table,
            clickableColumns,
            onRowClick,
            rowValidationSchema,
          })
        : rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];

            if (!row) return null;

            return (
              <TableBodyRow
                key={row.id}
                row={row}
                virtualRow={virtualRow}
                rowVirtualizer={rowVirtualizer}
                table={table}
                clickableColumns={clickableColumns}
                onRowClick={onRowClick}
                rowValidationSchema={rowValidationSchema}
                stickyColumns={stickyColumns}
                getSubRowStyle={getSubRowStyle}
                bordered={bordered}
                className={rowClassName}
              />
            );
          })}
    </RawTableBody>
  );
};

function VirtualizedTableComponent<T>(
  props: Props<T>,
  ref: React.ForwardedRef<HTMLTableElement>,
) {
  const {
    onRowClick = noop,
    table,
    tableContainerRef,
    clickableColumns = [],
    estimateSize = 49,
    header = "sticky",
    rowValidationSchema,
    loading,
    stickyColumns,
    children,
    footer = false,
    hideHeader = false,
    getSubRowStyle,
    bordered = false,
    rowClassName,
    getEmptyStateComponent,
  } = props;
  const [containerReady, setContainerReady] = useState(false);
  const rows = table.getRowModel().rows;
  const pinnedRows = table.getState().rowPinning;

  const sortedRows = useMemo(() => {
    let topRows: Row<T>[] = [];
    let centerRows: Row<T>[] = [];
    let bottomRows: Row<T>[] = [];

    try {
      topRows = table.getTopRows?.() ?? [];
    } catch (error) {
      topRows = [];
    }

    try {
      centerRows = table.getCenterRows?.() ?? table.getRowModel().rows;
    } catch (error) {
      centerRows = [];
    }

    try {
      bottomRows = table.getBottomRows?.() ?? [];
    } catch (error) {
      bottomRows = [];
    }
    return [...topRows, ...centerRows, ...bottomRows];
  }, [rows, pinnedRows]);

  useEffect(() => {
    if (tableContainerRef.current && !containerReady) {
      setContainerReady(true);
    }
  }, [tableContainerRef.current, containerReady]);

  const rowVirtualizer = useVirtualizer({
    count: sortedRows.length,
    getItemKey: (index) => sortedRows[index]?.id ?? index,
    estimateSize: () => estimateSize,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    indexAttribute: "data-index",
    overscan: 5,
  });

  if (!containerReady) {
    return null;
  }

  const footerElement =
    typeof footer === "boolean" ? (
      footer ? (
        <div className="h-10" />
      ) : null
    ) : (
      footer
    );

  return (
    <table ref={ref} className="w-full caption-bottom text-sm">
      {!hideHeader ? (
        <TableHeader
          className={cn(
            header === "sticky" && "sticky top-0 bg-background z-[1] px-0.5",
          )}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              style={{ display: "flex", alignItems: "center" }}
            >
              {headerGroup.headers.map((header, headerIndex) => {
                const hasSize = header.column.columnDef.size !== undefined;

                const {
                  isStickyLeft,
                  isStickyRight,
                  leftOffset,
                  isRightmostLeftSticky,
                  isLeftmostRightSticky,
                } = getStickyColumnPosition(
                  header.column.id,
                  headerGroup.headers,
                  stickyColumns,
                );

                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    role={header.id === "select" ? "checkbox" : undefined}
                    className={cn(
                      "min-w-[100px] flex-[1_1_0]",
                      bordered && "border-r border-border last:border-r-0",
                      header.column.columnDef.meta?.className,
                      hasSize && "flex-none",
                      isStickyLeft &&
                        "sticky z-10 bg-background pr-4 self-stretch flex items-center",
                      isStickyRight &&
                        "sticky right-0 z-10 bg-background pl-4 self-stretch flex items-center",
                      isRightmostLeftSticky && "border-r-2 border-r-border",
                      isLeftmostRightSticky && "border-l-2 border-l-border",
                      header.id === "select" && "justify-center",
                    )}
                    style={{
                      width: header.column.columnDef.size,
                      minWidth:
                        header.column.columnDef.minSize ??
                        header.column.columnDef.size,
                      maxWidth:
                        header.column.columnDef.maxSize ===
                        Number.MAX_SAFE_INTEGER
                          ? header.column.columnDef.size
                          : header.column.columnDef.maxSize,
                      ...(isStickyLeft && { left: `${leftOffset}px` }),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
      ) : null}
      <TableBody<T>
        rowVirtualizer={rowVirtualizer}
        rows={sortedRows}
        table={table}
        loading={loading}
        rowValidationSchema={rowValidationSchema}
        clickableColumns={clickableColumns}
        onRowClick={onRowClick}
        stickyColumns={stickyColumns}
        getSubRowStyle={getSubRowStyle}
        bordered={bordered}
        rowClassName={rowClassName}
        getEmptyStateComponent={getEmptyStateComponent}
      >
        {children && ((props) => children(props))}
      </TableBody>
      {footerElement}
    </table>
  );
}

export const VirtualizedTable = forwardRef(VirtualizedTableComponent) as <
  T = RowData,
>(
  props: Props<T> & { ref?: React.ForwardedRef<HTMLTableElement> },
) => React.ReactElement;
